import { useMemo, useState } from "react";
import { useLogoStore } from "../../state/logoStore";
import { renderLogoSVG } from "../../engine/render";
import { scoreLogo } from "../../engine/aestheticScore";
import { generateVariations, type ExtractedColor, type ColorAssignment } from "../../engine/colorExtractor";
import { ImageUploader } from "../ImageUploader";

// ============================================================
// Palette-from-image — collapsible upload that extracts colors
// and surfaces them right in the Palette section.
// ============================================================
export function PaletteFromImage() {
  const [open, setOpen] = useState(false);
  const palette = useLogoStore((s) => s.referencePalette);

  return (
    <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px dashed var(--border)" }}>
      <button
        className="btn"
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        onClick={() => setOpen((v) => !v)}
      >
        <span>🎨 Sample colors from an image</span>
        <span style={{ opacity: 0.6 }}>{palette.length > 0 ? `${palette.length} colors · ${open ? "−" : "+"}` : (open ? "−" : "+")}</span>
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          <ImagePalettePanel />
        </div>
      )}
    </div>
  );
}

// ============================================================
// Image → palette extractor + auto-generated logo variations
// Reads the palette from the global store (set on Start page or here).
// ============================================================
export function ImagePalettePanel() {
  const setStyle = useLogoStore((s) => s.setStyle);
  const palette = useLogoStore((s) => s.referencePalette);
  const current = useLogoStore((s) => ({
    title: s.title, slogan: s.slogan, iconId: s.iconId, qrText: s.qrText, layout: s.layout,
    titleFont: s.titleFont, sloganFont: s.sloganFont,
    titleColor: s.titleColor, sloganColor: s.sloganColor, iconColor: s.iconColor,
    background: s.background, titleSize: s.titleSize, sloganSize: s.sloganSize, iconScale: s.iconScale,
  }));

  const variations: ColorAssignment[] = useMemo(() => {
    if (palette.length === 0) return [];
    const raw = generateVariations(palette, 12);
    const scored = raw.map((v) => ({
      v,
      score: scoreLogo({ state: { ...current, ...v, customIcons: [] } }).score,
    }));
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 8).map((x) => x.v);
  }, [palette, current]);

  function applyColorTo(target: "background" | "titleColor" | "sloganColor" | "iconColor", hex: string) {
    setStyle({ [target]: hex });
  }

  return (
    <>
      <div className="field">
        <span>Reference image</span>
        <ImageUploader />
      </div>

      {palette.length > 0 && (
        <>
          <div className="field" style={{ marginTop: 16 }}>
            <span>Click any color → apply to…</span>
            <div className="palette-grid">
              {palette.map((c) => (
                <PaletteSwatch key={c.hex} color={c} onApply={applyColorTo} />
              ))}
            </div>
          </div>

          <div className="field" style={{ marginTop: 16 }}>
            <span>Logo variations · click to apply</span>
            <div className="variations-grid">
              {variations.map((v, i) => (
                <button
                  key={i}
                  className="variation-card"
                  onClick={() => setStyle(v)}
                  title={`Variation ${i + 1}`}
                >
                  <div
                    className="variation-svg"
                    dangerouslySetInnerHTML={{
                      __html: renderLogoSVG({ ...current, ...v }, { width: 200, height: 200 }),
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function PaletteSwatch({ color, onApply }: {
  color: ExtractedColor;
  onApply: (target: "background" | "titleColor" | "sloganColor" | "iconColor", hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const labelColor = color.lightness > 0.55 ? "#0f172a" : "#ffffff";
  return (
    <div className="palette-cell">
      <button
        className="palette-swatch"
        style={{ background: color.hex, color: labelColor }}
        onClick={() => setOpen((v) => !v)}
        title={`${color.hex}  ·  click to apply`}
      >
        {color.hex.toUpperCase()}
      </button>
      {open && (
        <div className="palette-actions">
          <button onClick={() => { onApply("titleColor", color.hex); setOpen(false); }}>Title</button>
          <button onClick={() => { onApply("sloganColor", color.hex); setOpen(false); }}>Slogan</button>
          <button onClick={() => { onApply("iconColor", color.hex); setOpen(false); }}>Icon</button>
          <button onClick={() => { onApply("background", color.hex); setOpen(false); }}>BG</button>
        </div>
      )}
    </div>
  );
}
